import { useCallback, useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { skipToken } from '@apollo/client/react';
import type { ZonedDateTime } from '@js-joda/core';
import type { OdProtosWebData_ParticipantInput } from '__generated__/globalTypes';
import { OdProtosWebData_ParticipantRole } from '__generated__/globalTypes';
import type { MutateResult, QueryResult, UseMutationResult } from 'platform/apollo';
import { useAuthenticatedQuery, useMutation } from 'platform/apollo';
import { useAuthAuthenticated } from 'platform/components/AuthContext';
import type {
  DeleteParticipantMutation,
  ParticipantsQuery,
  ParticipantsQueryVariables,
  ResendParticipantInviteMutation,
  UpsertParticipantMutation,
} from 'sellDirect/graphql/__generated__/useParticipants';
import {
  DeleteParticipantDocument,
  ParticipantsDocument,
  ResendParticipantInviteDocument,
  UpsertParticipantDocument,
} from 'sellDirect/graphql/__generated__/useParticipants';
import type { ParticipantFragment } from 'sellDirect/graphql/fragments/__generated__/ParticipantFragment';
import { systemZonedDateTimeFromString } from 'shared/helpers/datetime';

// Keep this in sync with MAX_SIGNATORIES in Web Participant model.
// https://github.com/opendoor-labs/web/blob/4d3fe59573f6adadb758398b45a90b809261c511/app/models/participant.rb#L7
const MAX_SIGNATORIES = 2;

gql`
  query Participants($sellerInputUUID: String!) {
    sellDirect {
      getParticipants(input: { lead: { source: { type: RECEPTION, id: $sellerInputUUID } } }) {
        participants {
          participants {
            ...Participant
          }
        }
      }
    }
  }
`;

gql`
  mutation UpsertParticipant(
    $sellerInputUUID: String!
    $participants: [OdProtosWebData_ParticipantInput!]!
  ) {
    sellDirect {
      upsertParticipant(
        input: {
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
          participants: { participants: $participants }
        }
      ) {
        participants {
          participants {
            ...Participant
          }
        }
      }
    }
  }
`;

gql`
  mutation DeleteParticipant($sellerInputUUID: String!, $participantId: String!) {
    sellDirect {
      deleteParticipant(
        input: {
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
          participantId: $participantId
        }
      ) {
        participants {
          participants {
            ...Participant
          }
        }
      }
    }
  }
`;

// Adding __typename in the response because this doesn't get picked up by yarn generate if it doesn't return anything
gql`
  mutation ResendParticipantInvite($sellerInputUUID: String!, $participantId: String!) {
    sellDirect {
      resendParticipantInvite(
        input: {
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
          participantId: $participantId
        }
      ) {
        __typename
      }
    }
  }
`;

export interface Participant extends Omit<ParticipantFragment, 'createdAt'> {
  createdAt: ZonedDateTime; // generated GraphQL type is any, so Omit it and replace with ZonedDateTime

  isInitiator: boolean;
  isSignatory: boolean;
  isCurrentUser: boolean;
}

export interface UseParticipantsResult {
  /** Participants. Undefined if not yet known. */
  participants: Participant[] | undefined;
  /** If current user is initiator of lead. Undefined if not yet known. */
  currentUserIsInitiator: boolean | undefined;
  /** If current user is a participant of a lead. An initiator is also a participant. Undefined if not yet known. */
  currentUserIsParticipant: boolean | undefined;
  /** If another signatory can be added. Max signatories is 2. Undefined if not yet known. */
  canAddSignatory: boolean | undefined;
  /** Set when an email-change upsert causes the API to create a new participant instead of updating in-place. */
  lastReplacedParticipant: { oldId: string; newId: string } | undefined;

  getResult: QueryResult<ParticipantsQuery, ParticipantsQueryVariables>;
  upsertParticipant: (
    participant: OdProtosWebData_ParticipantInput,
  ) => Promise<MutateResult<UpsertParticipantMutation>>;
  upsertResult: UseMutationResult<UpsertParticipantMutation>;
  deleteParticipant: (participantId: string) => Promise<MutateResult<DeleteParticipantMutation>>;
  deleteResult: UseMutationResult<DeleteParticipantMutation>;
  resendParticipantInvite: (
    participantId: string,
  ) => Promise<MutateResult<ResendParticipantInviteMutation>>;
  resendParticipantInviteResult: UseMutationResult<ResendParticipantInviteMutation>;
}

/**
 * Manages participant state and provides mutation functions.
 */
export const useParticipants = (sellerInputUUID: string): UseParticipantsResult => {
  const { user: currentHuman } = useAuthAuthenticated();
  const [mutatedGqlParticipants, setMutatedGqlParticipants] = useState<ParticipantFragment[]>();
  const [lastReplacedParticipant, setLastReplacedParticipant] = useState<
    { oldId: string; newId: string } | undefined
  >();

  const getResult = useAuthenticatedQuery(
    ParticipantsDocument,
    !sellerInputUUID?.trim()
      ? skipToken
      : {
          variables: {
            sellerInputUUID,
          },
          context: { linkContext: { team: 'seller' } },
        },
  );

  useEffect(() => {
    if (getResult.data) {
      setMutatedGqlParticipants(undefined);
      setLastReplacedParticipant(undefined);
    }
  }, [getResult.data]);

  const [upsertParticipantGql, upsertResult] = useMutation(UpsertParticipantDocument, {
    onCompleted: (data) => {
      setMutatedGqlParticipants(data.sellDirect.upsertParticipant?.participants?.participants);
    },
    context: { linkContext: { team: 'seller' } },
  });
  const [deleteParticipantGql, deleteResult] = useMutation(DeleteParticipantDocument, {
    onCompleted: (data: DeleteParticipantMutation) =>
      setMutatedGqlParticipants(data.sellDirect.deleteParticipant?.participants?.participants),
    context: { linkContext: { team: 'seller' } },
  });

  const [resendParticipantInviteGql, resendParticipantInviteResult] = useMutation(
    ResendParticipantInviteDocument,
    {
      onCompleted: (data: ResendParticipantInviteMutation) => data,
      fetchPolicy: 'no-cache',
      context: { linkContext: { team: 'seller' } },
    },
  );

  const upsertParticipant = useCallback(
    async (participant: OdProtosWebData_ParticipantInput) => {
      const result = await upsertParticipantGql({
        variables: { sellerInputUUID, participants: [participant] },
      });

      // When a participant's email changes, the BE deletes the old participant and creates a new
      // one. Map the old ID to the new ID so the open dialog follows the replacement.
      if (!result.error && result.data && participant.id) {
        const responseParticipants =
          result.data.sellDirect.upsertParticipant?.participants?.participants ?? [];
        const oldStillExists = responseParticipants.some((p) => p.id === participant.id);
        if (!oldStillExists && participant.email) {
          const replacement = responseParticipants.find((p) =>
            emailsMatch(p.email, participant.email ?? null),
          );
          if (replacement) {
            setLastReplacedParticipant({ oldId: participant.id, newId: replacement.id });
          }
        }
      }

      return result;
    },
    [sellerInputUUID, upsertParticipantGql],
  );

  const deleteParticipant = useCallback(
    (participantId: string) =>
      deleteParticipantGql({
        variables: {
          sellerInputUUID,
          participantId,
        },
      }),
    [deleteParticipantGql, sellerInputUUID],
  );

  const resendParticipantInvite = useCallback(
    (participantId: string) =>
      resendParticipantInviteGql({
        variables: {
          sellerInputUUID,
          participantId,
        },
      }),
    [resendParticipantInviteGql, sellerInputUUID],
  );

  const participants = useMemo(() => {
    // Prefer mutated participants, otherwise use original fetched participants.
    const gqlParticipants =
      mutatedGqlParticipants ||
      getResult?.data?.sellDirect.getParticipants?.participants?.participants;

    if (!gqlParticipants) {
      return undefined;
    }

    return (
      gqlParticipants
        // Filter out seller participants
        .filter((p) => p.roles.includes(OdProtosWebData_ParticipantRole.SELLER_PARTICIPANT))
        // Add computed Participant fields.
        .map<Participant>(({ createdAt, ...p }) => {
          return {
            ...p,
            createdAt: systemZonedDateTimeFromString(createdAt),
            isInitiator: p.roles.includes(OdProtosWebData_ParticipantRole.INITIATOR),
            isSignatory: p.roles.includes(OdProtosWebData_ParticipantRole.SIGNATORY),
            isCurrentUser: emailsMatch(p.email, currentHuman?.email),
          };
        })
    );
  }, [currentHuman, getResult, mutatedGqlParticipants]);

  const { currentUserIsInitiator, currentUserIsParticipant } = useMemo(() => {
    if (!participants) {
      return {
        currentUserIsInitiator: undefined,
        currentUserIsParticipant: undefined,
      };
    }

    const curParticipant = participants.find((p) => emailsMatch(p.email, currentHuman?.email));
    if (!curParticipant) {
      return {
        currentUserIsInitiator: false,
        currentUserIsParticipant: false,
      };
    }

    return {
      currentUserIsInitiator: curParticipant.isInitiator,
      currentUserIsParticipant: true,
    };
  }, [currentHuman, participants]);

  const canAddSignatory = useMemo(() => {
    if (!participants) {
      return undefined;
    }

    const signatories = participants.filter((p) => p.isSignatory);
    return signatories.length < MAX_SIGNATORIES;
  }, [participants]);

  return {
    participants,
    canAddSignatory,
    currentUserIsInitiator,
    currentUserIsParticipant,
    lastReplacedParticipant,
    getResult,
    upsertParticipant,
    upsertResult,
    deleteParticipant,
    deleteResult,
    resendParticipantInvite,
    resendParticipantInviteResult,
  };
};

/** Returns true if both strings are present and compares them case insensitively. */
const emailsMatch = (s1: string | null | undefined, s2: string | null | undefined): boolean => {
  if (!s1 || !s2) {
    return false;
  }
  return s1.toLowerCase() === s2.toLowerCase();
};
